import type { Validator } from 'api/types';
import ErrorMessage from 'components/NewLayout/ErrorMessage';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export interface DropdownOptionType {
  value: string;
  id: string | number;
  color?: string;
  label?: string;
  vendor_id?: string;
}

export default function DropDown({
  labelText,
  name,
  value,
  data,
  onChange,
  isRequired,
  className,
  optionText,
  disabled,
  validator,
  validationString,
  onBlur,
  isDropdownOptionListVisible
}: {
  labelText?: string;
  name: string;
  value?: string | number;
  onChange: (event: { target: { name: string; value: string } }) => void;
  data?: DropdownOptionType[];
  isRequired?: boolean | string;
  className?: string;
  optionText?: string;
  disabled?: boolean;
  validator?: Validator;
  validationString?: string;
  onBlur?: (event: { target: { name: string; value: string } }) => void;
  onKeyDown?: null;
  isDropdownOptionListVisible?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState<DropdownOptionType[]>([]);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0
  });
  const justSelectedRef = useRef(false);
  const touchMovedRef = useRef(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const optionsRef = useRef<HTMLDivElement | null>(null);

  const selectOptions = data?.map((option) => ({
    value: option.id,
    label: option.value,
    color: option.color
  }));

  const sortedOptions = useMemo(
    () => (name.includes('project') ? selectOptions : sortDropdownOptions(selectOptions, 'value')),
    [selectOptions, name]
  );

  useEffect(() => {
    if (isDropdownOptionListVisible && inputRef.current) {
      inputRef.current.focus();
      setIsOpen(true);
    }
  }, [isDropdownOptionListVisible]);

  useEffect(() => {
    const filtered = sortedOptions.filter((option) =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredOptions(filtered);
    setFocusedIndex(-1);
  }, [searchTerm, sortedOptions]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isError = validator?.current?.message(
    name,
    value?.toString(),
    validationString || 'required'
  );

  const selectedOption = sortedOptions?.find(
    (option) => option.value.toString() === value?.toString()
  );

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setIsOpen(true);
  };

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  const handleOptionClick = (option) => {
    justSelectedRef.current = true;

    const valueStr = option.value.toString();

    onChange({ target: { name, value: valueStr } });

    if (onBlur) {
      onBlur({ target: { name, value: valueStr } });
    }

    setSearchTerm('');
    setIsOpen(false);

    setTimeout(() => {
      justSelectedRef.current = false;
    }, 300);
  };

  const handleInputBlur = () => {
    setTimeout(() => {
      if (onBlur) {
        onBlur({ target: { name, value: value?.toString() || '' } });
      }
    }, 150);
  };

  const handleSafeOptionClick = (option) => {
    if (!justSelectedRef.current) {
      handleOptionClick(option);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setFocusedIndex((prev) => (prev < filteredOptions.length - 1 ? prev + 1 : 0));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setFocusedIndex((prev) => (prev > 0 ? prev - 1 : filteredOptions.length - 1));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (focusedIndex >= 0 && filteredOptions[focusedIndex]) {
        handleOptionClick(filteredOptions[focusedIndex]);
      }
    } else if (event.key === 'Escape') {
      setIsOpen(false);
      setSearchTerm('');
    }
  };
  useEffect(() => {
    if (isOpen && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
  }, [isOpen]);

  const displayValue = isOpen ? searchTerm : selectedOption?.label || '';

  return (
    <div
      ref={containerRef}
      className="dropdown-container"
      style={{ position: 'relative', zIndex: 10000 }}>
      {labelText !== undefined && (
        <label className="form-label" htmlFor={name}>
          {labelText}
          {isRequired !== false && <span className="required">*</span>}
        </label>
      )}

      <div className="dropdown-input-container" style={{ position: 'relative' }}>
        <input
          ref={inputRef}
          id={name}
          name={name}
          type="text"
          className={`${className} ${
            isRequired && isError ? 'red-border' : ''
          } form-control dropdown-input`}
          value={displayValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          onKeyDown={handleKeyDown}
          placeholder={optionText || 'Search or select an option'}
          disabled={disabled}
          autoComplete="off"
          style={{
            backgroundColor: selectedOption?.color || '',
            color: selectedOption?.color ? '#fff' : '#000',
            paddingRight: '30px'
          }}
        />

        <div
          className="dropdown-arrow"
          style={{
            position: 'absolute',
            right: '10px',
            top: '50%',
            transform: 'translateY(-50%)',
            pointerEvents: 'none',
            fontSize: '12px',
            color: '#666'
          }}>
          {isOpen ? '▲' : '▼'}
        </div>
      </div>
      {isOpen &&
        createPortal(
          <div
            ref={optionsRef}
            className="dropdown-options"
            style={{
              position: 'absolute',
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              width: dropdownPosition.width,
              backgroundColor: '#fff',
              border: '1px solid #ddd',
              borderRadius: '4px',
              maxHeight: '200px',
              overflowY: 'auto',
              zIndex: 99999,
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}>
            {filteredOptions.length === 0 ? (
              <div
                className="dropdown-option no-options"
                style={{
                  padding: '10px',
                  color: '#999',
                  fontStyle: 'italic'
                }}>
                No options available
              </div>
            ) : (
              filteredOptions.map((option, index) => (
                <div
                  key={option.value}
                  className={`dropdown-option ${index === focusedIndex ? 'focused' : ''}`}
                  style={{
                    padding: '10px',
                    cursor: 'pointer',
                    backgroundColor: index === focusedIndex ? '#f0f0f0' : option.color || '#fff',
                    color: option.color ? '#fff' : '#000',
                    borderBottom: index < filteredOptions.length - 1 ? '1px solid #eee' : 'none'
                  }}
                  onTouchStart={() => {
                    touchMovedRef.current = false;
                  }}
                  onTouchMove={() => {
                    touchMovedRef.current = true;
                  }}
                  onTouchEnd={() => {
                    if (!touchMovedRef.current) {
                      handleSafeOptionClick(option);
                    }
                  }}
                  onMouseDown={() => handleSafeOptionClick(option)}
                  onMouseEnter={() => setFocusedIndex(index)}>
                  {option.label}
                </div>
              ))
            )}
          </div>,
          document.body
        )}

      {isRequired && validator && isError ? (
        <ErrorMessage
          validator={validator}
          value={value}
          label={name}
          validationString={validationString || ''}
        />
      ) : (
        <p />
      )}
    </div>
  );
}

export function sortDropdownOptions(options, field = 'id') {
  return options?.sort(function (a, b) {
    if (a[field] && b[field]) {
      const valueA = a[field === 'id' ? 'value' : 'label'].toLowerCase();
      const valueB = b[field === 'id' ? 'value' : 'label'].toLowerCase();
      if (valueA < valueB) {
        return -1;
      }
      if (valueA > valueB) {
        return 1;
      }
      return 0;
    }
    return 0;
  });
}
